// The date game's rules, played entirely in the browser.
//
// A game is a static, pre-generated tree (promptchan-backend docs/architecture/date-game.md): each
// choice names the node it leads to, there are no hidden meters, and every image already exists.
// So "playing" is walking that tree. This module is that walk and nothing else — no React, no
// fetch, no storage — so the rules can be tested with `node --test` against a fixture game
// (src/lib/__tests__/date-game.test.mjs) instead of a dev-only fixture path in production code.
//
// The client game is GET /api/date_game/<id>'s `game`: the published Game with the bible and the
// per-shot generation fields stripped. Node kinds:
//   scene  — shot.image_url, narration, line, 2-3 choices {id, label, reply, to}
//   ending — shot.image_url, ending {kind: 'fail'|'win', title, recap, hint}
//   sex    — exactly one, terminal; reaching it IS the win. `sex` is either
//            {locked: false, buildup_seconds, afterglow, line, positions[{line, still_url, video_url}], climax_url}
//            or, for Free/guests, {locked: true, reason: 'plan', poster_url} — the server withholds
//            every other URL — or, in safe mode, {locked: true, reason: 'safe_mode', poster_url: null}.
// In safe mode (GET ...?safe=1) an explicit shot also arrives as {image_url: null, hidden: 'safe_mode'}:
// the player shows black there and plays the text as normal.
//
// v2 (schema_version 2, 2026-10-03 — "chapters", promptchan-backend contract date_game v2): the same
// walk plus acts (cards on entering an act's start), memories (a choice `sets` some; a choice that
// `requires` memories not held is shown locked with its `locked_hint`), `reached` rungs (a toast),
// small-win clips on scene shots (`shot.video_url`), and `milestone` nodes in place of `sex`: the
// same scene, with several `finishes` the player chooses between. `sexOf` normalises both shapes,
// so everything below the walk reads one shape. v1 dates play exactly as before.
//
// v2 endings (2026-10-04, promptchan-backend check_chapter): an ending is a fail
//   {kind: 'fail', title, recap, hint, caught?: true}  — `caught` = found out by her family; still a
//   fail in every way, the card only looks different;
// or a WIN that is not a sex scene
//   {kind: 'win', title, recap, hint, cliffhanger}     — she kisses you goodnight: the chapter is won
//   exactly as a milestone wins it (the server counts it and carries it into the next chapter).

// RESUME_GEMS on the backend is a dial; the response's `resume_gems` is what the button shows.
// This is only the fallback for a response that omits it — kept equal to the backend's (2 since
// 2026-10-06, was 5).
export const DEFAULT_RESUME_GEMS = 2

const uniq = (a) => [...new Set(a.filter((x) => typeof x === 'string'))]

// The sex scene's build-up before "Finish" unlocks, when the game does not say.
export const DEFAULT_BUILDUP_SECONDS = 20

export function getNode(game, id) {
  if (!game || !game.nodes || typeof id !== 'string') return null
  return Object.prototype.hasOwnProperty.call(game.nodes, id)
    ? game.nodes[id]
    : null
}

// What the player renders for a node: 'scene' | 'sex' | 'fail' | 'win' | null (unknown node).
// An ending without an explicit kind is treated as a fail: crediting a win by accident is the
// worse mistake (it would also post an `ended` the backend may count as won).
export function nodeView(node) {
  if (!node) return null
  if (node.kind === 'scene') return 'scene'
  if (node.kind === 'sex' || node.kind === 'milestone') return 'sex'
  if (node.kind === 'ending')
    return node.ending && node.ending.kind === 'win' ? 'win' : 'fail'
  return null
}

export function isTerminal(node) {
  const view = nodeView(node)
  return view === 'sex' || view === 'win' || view === 'fail'
}

// A fail ending where she got caught (2026-10-04): styled apart, otherwise a fail. Only an
// explicit `caught: true` on a fail counts.
export function isCaught(node) {
  return nodeView(node) === 'fail' && node.ending?.caught === true
}

export function isCheckpoint(node) {
  return Boolean(node && node.kind === 'scene' && node.checkpoint)
}

// The sex / milestone scene in one shape, v1 or v2, or null for any other node:
//   locked:   { locked: true, reason: 'plan' | 'safe_mode', poster_url, poster_video_url, title }
//             (poster_video_url: v2's blurred teaser loop, made by the server like the poster; plan only)
//   unlocked: { locked: false, v, title, line, buildup_seconds, recap, cliffhanger, best,
//               positions: [{ ask, line, still_url, video_url }],
//               finishes:  [{ id, label, line, climax_url, climax_video_url, finale_url, afterglow }] }
// v1's single climax/finale becomes one finish without a label (no finish choice is offered for
// v1). A v2 shot field may arrive as a Shot ({image_url}) or a flat *_url; both are read.
const shotUrl = (flat, shot) => flat || (shot && shot.image_url) || null

export function sexOf(node) {
  if (!node) return null
  if (node.kind === 'sex') {
    const s = node.sex
    // Anything other than an explicit `locked: false` is locked: a missing or malformed `sex`
    // must never be treated as an unlocked scene with no media.
    if (!s || s.locked !== false)
      return {
        locked: true,
        reason: s && s.reason === 'safe_mode' ? 'safe_mode' : 'plan',
        poster_url: (s && s.poster_url) || null,
        poster_video_url: null,
        title: null,
      }
    return {
      locked: false,
      v: 1,
      title: null,
      line: s.line || null,
      buildup_seconds: s.buildup_seconds,
      recap: null,
      cliffhanger: null,
      best: false,
      positions: Array.isArray(s.positions) ? s.positions : [],
      finishes: [
        {
          id: 'finish',
          label: null,
          line: null,
          climax_url: s.climax_url || null,
          climax_video_url: s.climax_video_url || null,
          finale_url: s.finale_url || null,
          afterglow: s.afterglow || '',
        },
      ],
    }
  }
  if (node.kind === 'milestone') {
    const m = node.milestone
    const positions = (m && Array.isArray(m.positions) ? m.positions : [])
      .filter(Boolean)
      .map((p) => ({
        ask: p.ask,
        line: p.line,
        still_url: shotUrl(p.still_url, p.still),
        video_url: p.video_url || null,
      }))
    const finishes = (m && Array.isArray(m.finishes) ? m.finishes : [])
      .filter(Boolean)
      .map((f, i) => ({
        id: f.id || `finish${i}`,
        label: f.label || null,
        line: f.line || null,
        climax_url: shotUrl(f.climax_url, f.climax),
        climax_video_url: f.climax_video_url || null,
        finale_url: shotUrl(f.finale_url, f.finale),
        afterglow: f.afterglow || '',
      }))
    if (!m || m.locked === true || (!positions.length && !finishes.length))
      return {
        locked: true,
        reason: m && m.reason === 'safe_mode' ? 'safe_mode' : 'plan',
        poster_url: (m && m.poster_url) || null,
        poster_video_url:
          (m && m.reason !== 'safe_mode' && m.poster_video_url) || null,
        title: (m && m.title) || null,
      }
    return {
      locked: false,
      v: 2,
      title: m.title || null,
      line: m.line || null,
      buildup_seconds: m.buildup_seconds,
      recap: m.recap || null,
      cliffhanger: m.cliffhanger || null,
      best: Boolean(m.best),
      positions,
      finishes,
    }
  }
  return null
}

export function isSexLocked(node) {
  const s = sexOf(node)
  return !s || s.locked
}

// Why the sex scene is locked: 'plan' (Free/guest — the pre-blurred poster and an upgrade
// offer) or 'safe_mode' (the viewer is in safe mode — no poster at all, no upgrade offer, since
// paying would not show it either). null when unlocked. A locked node without a reason predates
// the safe-mode change and is a plan lock.
export function sexLockReason(node) {
  const s = sexOf(node)
  if (!s || !s.locked) return null
  return s.reason
}

// A shot the server blacked out for a safe-mode viewer: {image_url: null, hidden: 'safe_mode'}.
// The scene still plays (narration, line, choices); only the picture is replaced.
export function isShotHidden(node) {
  return Boolean(node && node.shot && node.shot.hidden && !node.shot.image_url)
}

// Whether the screen for this node is blacked out for safe mode: a hidden shot, or the sex
// scene locked for safe mode.
export function isSafeHidden(node) {
  return isShotHidden(node) || sexLockReason(node) === 'safe_mode'
}

// Whether to ask the server for the safe-mode game (`?safe=1`) — the same signal that hides the
// Explore row. A safe country is always safe; a guest is safe (AuthContext defaults signed-out
// visitors to safe mode); a signed-in viewer follows their setting, and null means the setting
// has not loaded yet, so the caller waits rather than guessing.
export function requestSafeMode({ signedIn, safe, safeCountry }) {
  if (safeCountry) return true
  if (!signedIn) return true
  if (safe === null || safe === undefined) return null
  return Boolean(safe)
}

// The one picture that stands for a node — the backdrop while it is on screen and what gets
// preloaded before it is reached. null for a blacked-out (safe-mode) node.
export function nodeImage(node) {
  if (!node) return null
  const sex = sexOf(node)
  if (sex) {
    if (sex.locked) return sex.reason === 'plan' ? sex.poster_url : null
    return (sex.positions[0] && sex.positions[0].still_url) || null
  }
  return (node.shot && node.shot.image_url) || null
}

// Choices a scene offers, dropping any whose target is not in the tree (a broken edge would
// strand the player on a blank screen; better to show one fewer button).
export function choicesFor(game, nodeId) {
  const node = getNode(game, nodeId)
  if (!node || node.kind !== 'scene' || !Array.isArray(node.choices)) return []
  return node.choices.filter((c) => c && getNode(game, c.to))
}

// Tapping a choice: her instant reply, then the node it leads to. null if the choice is not
// offered here.
export function applyChoice(game, nodeId, choiceId, held) {
  const choice = choicesFor(game, nodeId).find((c) => c.id === choiceId)
  if (!choice) return null
  if (choiceLocked(choice, held)) return null
  const step = { reply: choice.reply || '', to: choice.to }
  // v2: the memories this choice sets (v1 choices have none, and the step stays as it was).
  const sets = Array.isArray(choice.sets)
    ? choice.sets.filter((x) => typeof x === 'string')
    : []
  if (sets.length) step.sets = sets
  return step
}

// Every image worth fetching before the player can need it: the backdrop of each node one tap
// away and, for an unlocked sex node, the other position stills, the climax and the finale. Videos are not
// preloaded here — the clips buffer themselves once the sex scene mounts.
export function preloadUrls(game, nodeId) {
  const urls = []
  const add = (u) => {
    if (u && !urls.includes(u)) urls.push(u)
  }
  for (const choice of choicesFor(game, nodeId)) {
    const next = getNode(game, choice.to)
    add(nodeImage(next))
    const sex = sexOf(next)
    if (sex && !sex.locked) {
      for (const p of sex.positions) add(p && p.still_url)
      for (const f of sex.finishes) {
        add(f.climax_url)
        add(f.finale_url)
      }
    }
  }
  return urls
}

// The unlocked sex node one tap away, if any. The player mounts its clips early (paused) so the
// tap that enters the scene can start them with sound inside that gesture, which is what iOS
// Safari requires for unmuted playback.
export function unlockedSexAhead(game, nodeId) {
  for (const choice of choicesFor(game, nodeId)) {
    const next = getNode(game, choice.to)
    const sex = sexOf(next)
    if (sex && !sex.locked) return choice.to
  }
  return null
}

// ---- a play ---------------------------------------------------------------------------------
// A play is { version, nodeId, path }: where the player is and how they got there this time.
// Restarting from the beginning is free and local; resuming from a checkpoint is a paid server
// call whose answer starts a new play at that checkpoint.

// v2 adds `memories` (held in this play: set by the choices taken, seeded from the previous
// chapter's carry) and `snapshots` (memories held on reaching each checkpoint — what a paid
// resume there restores). `schema`/`chapter` keep a saved play from restoring into another
// chapter or format.
export function startPlay(game, from, memories) {
  const start = from || (game && game.start)
  if (!getNode(game, start)) return null
  const play = { version: game.version ?? null, nodeId: start, path: [start] }
  if (isV2(game)) {
    play.schema = 2
    play.chapter = game.chapter ?? 1
    play.memories = uniq(Array.isArray(memories) ? memories : [])
    play.snapshots = {}
  }
  return play
}

export function advance(play, to, sets) {
  const next = { ...play, nodeId: to, path: [...(play.path || []), to] }
  if (play.schema === 2) {
    next.memories = uniq([
      ...(play.memories || []),
      ...(Array.isArray(sets) ? sets : []),
    ])
    next.snapshots = { ...(play.snapshots || {}), [to]: next.memories }
  }
  return next
}

// A play saved in the browser (so a reload or an iOS tab eviction does not throw the player back
// to the start). Accepted only if it belongs to this version of the game and every node on it
// still exists — a republished game gets a fresh start rather than a dangling node id.
export function restorePlay(game, saved) {
  if (!game || !saved || typeof saved !== 'object') return null
  if ((saved.version ?? null) !== (game.version ?? null)) return null
  if ((saved.schema ?? 1) !== (isV2(game) ? 2 : 1)) return null
  if (isV2(game) && (saved.chapter ?? 1) !== (game.chapter ?? 1)) return null
  const path = Array.isArray(saved.path) ? saved.path : null
  if (!path || path.length === 0) return null
  if (!path.every((id) => getNode(game, id))) return null
  if (saved.nodeId !== path[path.length - 1]) return null
  const play = {
    version: game.version ?? null,
    nodeId: saved.nodeId,
    path: [...path],
  }
  if (isV2(game)) {
    play.schema = 2
    play.chapter = game.chapter ?? 1
    play.memories = uniq(Array.isArray(saved.memories) ? saved.memories : [])
    play.snapshots =
      saved.snapshots && typeof saved.snapshots === 'object'
        ? saved.snapshots
        : {}
  }
  return play
}

// ---- progress -------------------------------------------------------------------------------
// The server keeps {checkpoints, endings, won, plays}; the browser keeps the same shape for
// guests and for the moment between reaching a node and the POST landing. The two are merged by
// union — progress only ever grows.

export function emptyProgress() {
  return { checkpoints: [], endings: [], won: false, plays: 0 }
}

export function mergeProgress(...sources) {
  const out = emptyProgress()
  for (const p of sources) {
    if (!p || typeof p !== 'object') continue
    out.checkpoints = uniq([
      ...out.checkpoints,
      ...(Array.isArray(p.checkpoints) ? p.checkpoints : []),
    ])
    out.endings = uniq([
      ...out.endings,
      ...(Array.isArray(p.endings) ? p.endings : []),
    ])
    out.won = out.won || Boolean(p.won)
    out.plays = Math.max(out.plays, Number.isFinite(p.plays) ? p.plays : 0)
  }
  return out
}

// What reaching a node means to the progress API: a checkpoint is `reached`; a terminal node —
// a fail ending, a win ending, or the sex node (which is the win) — is `ended`. Everything else
// posts nothing.
// A v2 win ending also sends the memories `held` in this play (2026-10-04): it is posted once,
// with no finish post after it to carry them, and the server writes them into the next
// chapter's carry (without them it would carry every memory ever made in the chapter).
export function progressEvent(game, nodeId, held) {
  const node = getNode(game, nodeId)
  if (isCheckpoint(node)) return { event: 'reached', node_id: nodeId }
  if (isTerminal(node)) {
    if (isV2(game) && nodeView(node) === 'win')
      return {
        event: 'ended',
        node_id: nodeId,
        memories: uniq(Array.isArray(held) ? held : []),
      }
    return { event: 'ended', node_id: nodeId }
  }
  return null
}

// The local mirror of what the server records for that event.
export function recordNode(progress, game, nodeId) {
  const node = getNode(game, nodeId)
  const base = mergeProgress(progress)
  if (isCheckpoint(node)) return mergeProgress(base, { checkpoints: [nodeId] })
  const view = nodeView(node)
  // v2 counts the milestone among the chapter's endings (the collection); v1 did not.
  if (view === 'sex')
    return mergeProgress(base, {
      won: true,
      endings: node.kind === 'milestone' ? [nodeId] : [],
    })
  if (view === 'win')
    return mergeProgress(base, { endings: [nodeId], won: true })
  if (view === 'fail') return mergeProgress(base, { endings: [nodeId] })
  return base
}

// ---- resume ---------------------------------------------------------------------------------

// Shortest number of taps from `from` to `to` following choices, or Infinity.
export function distance(game, from, to) {
  if (!getNode(game, from) || !getNode(game, to)) return Infinity
  const seen = new Set([from])
  let frontier = [from]
  for (let d = 0; frontier.length; d++) {
    if (frontier.includes(to)) return d
    const next = []
    for (const id of frontier) {
      for (const c of choicesFor(game, id)) {
        if (!seen.has(c.to)) {
          seen.add(c.to)
          next.push(c.to)
        }
      }
    }
    frontier = next
  }
  return Infinity
}

// Where "Resume from checkpoint" would put the player after this play failed, or null if it
// should not be offered.
//   1. the last checkpoint passed in THIS play — the one just before the mistake;
//   2. else a checkpoint reached in a previous play that leads to this fail, the nearest one;
//   3. else the furthest-in checkpoint reached in a previous play.
// The start node is a free restart, never a paid resume, so it is never returned.
export function resumeCheckpoint(game, play, progress) {
  if (!game || !play) return null
  const here = play.nodeId
  if (nodeView(getNode(game, here)) !== 'fail') return null
  const isResumable = (id) =>
    id !== game.start && isCheckpoint(getNode(game, id))

  const path = play.path || []
  for (let i = path.length - 1; i >= 0; i--) {
    if (isResumable(path[i])) return path[i]
  }

  const reached = (
    progress && Array.isArray(progress.checkpoints) ? progress.checkpoints : []
  ).filter(isResumable)
  if (reached.length === 0) return null

  let best = null
  let bestD = Infinity
  for (const id of reached) {
    const d = distance(game, id, here)
    if (d < bestD) {
      best = id
      bestD = d
    }
  }
  if (best) return best

  let deepest = null
  let deepestD = -1
  for (const id of reached) {
    const d = distance(game, game.start, id)
    if (d !== Infinity && d > deepestD) {
      deepest = id
      deepestD = d
    }
  }
  return deepest
}

// The buttons on an ending card. `resumeFrom` is the checkpoint id or null; `signedIn` decides
// whether Resume charges or sends the guest to sign up (the label is the same either way, so the
// price is visible before the account exists).
export function endingActions(game, play, progress) {
  const view = nodeView(getNode(game, play && play.nodeId))
  if (view === 'fail') {
    return {
      tryAgain: true,
      resumeFrom: resumeCheckpoint(game, play, progress),
      playAgain: false,
    }
  }
  if (view === 'win' || view === 'sex')
    return { tryAgain: false, resumeFrom: null, playAgain: true }
  return { tryAgain: false, resumeFrom: null, playAgain: false }
}

// ---- pacing ---------------------------------------------------------------------------------

// How long her reply stays up before the next scene: long enough to read, short enough that a
// one-liner does not stall. ~45 ms a character on a 1.4 s base, between 1.8 s and 4.5 s. A tap
// skips it.
export function replyDuration(text) {
  const n = typeof text === 'string' ? text.length : 0
  return Math.min(4500, Math.max(1800, 1400 + n * 45))
}

// The sex scene's build-up: seconds left and whether "Finish" is enabled.
export function buildupState(startedAt, now, seconds) {
  const total =
    Number.isFinite(seconds) && seconds >= 0 ? seconds : DEFAULT_BUILDUP_SECONDS
  const elapsed = Math.max(0, (now - startedAt) / 1000)
  const remaining = Math.max(0, Math.ceil(total - elapsed))
  return {
    remaining,
    ready: remaining === 0,
    fraction: total === 0 ? 1 : Math.min(1, elapsed / total),
  }
}

// Her lines carry stage directions in asterisks ("*looks confused* Why are you…"). Split them so
// the action can be set apart from what she says, asterisks removed. An unclosed asterisk is
// left as text.
export function splitLine(text) {
  const s = typeof text === 'string' ? text : ''
  const parts = []
  let i = 0
  while (i < s.length) {
    const open = s.indexOf('*', i)
    const close = open === -1 ? -1 : s.indexOf('*', open + 1)
    if (open === -1 || close === -1) {
      parts.push({ type: 'speech', text: s.slice(i) })
      break
    }
    if (open > i) parts.push({ type: 'speech', text: s.slice(i, open) })
    parts.push({ type: 'action', text: s.slice(open + 1, close) })
    i = close + 1
  }
  return parts.map((p) => ({ ...p, text: p.text.trim() })).filter((p) => p.text)
}

// ---- the dialogue box (visual-novel reveal) ---------------------------------------------------
// Each node is told as a short run of PAGES shown one at a time in the dialogue box, the way
// Ren'Py games do it: narration first (no name plate, italic), then her line (name plate). A
// reply after a choice, the sex scene's lines and the afterglow are one-page runs of her lines.
// Text types in; a tap (or Space/Enter) completes the page, the next tap moves on. The last
// page's text being complete is what lets the choices (or the ending card) appear.

// Typing speed, characters a second. Reduced motion types instantly (pass Infinity).
export const TYPE_CPS = 40

export function nodePages(node) {
  if (!node) return []
  const pages = []
  if (typeof node.narration === 'string' && node.narration.trim())
    pages.push({ kind: 'narration', text: node.narration.trim() })
  if (typeof node.line === 'string' && node.line.trim())
    pages.push({ kind: 'line', text: node.line.trim() })
  return pages
}

// Her lines (a reply, a position line, the afterglow): one page each, empties dropped.
export function linePages(...texts) {
  return texts
    .filter((t) => typeof t === 'string' && t.trim())
    .map((t) => ({ kind: 'line', text: t.trim() }))
}

// What a page shows: narration is plain text; her line is split into stage directions and
// speech (asterisks removed). Its length is what the typewriter counts.
export function pageParts(page) {
  if (!page) return []
  if (page.kind === 'narration') return [{ type: 'narration', text: page.text }]
  return splitLine(page.text)
}

// The parts as far as `n` characters have been revealed. Every part is returned, each with the
// visible prefix and the hidden rest, so the box can keep the full text's layout (the hidden rest
// is rendered invisibly) and its height never jumps while typing.
export function revealParts(parts, n) {
  let left = Math.max(0, n)
  return parts.map((p) => {
    const shown = p.text.slice(0, left)
    left -= shown.length
    return { ...p, shown, hidden: p.text.slice(shown.length) }
  })
}

const partsLength = (parts) => parts.reduce((sum, p) => sum + p.text.length, 0)

export function revealStart(now) {
  return { index: 0, startedAt: now, complete: false }
}

// Where the reveal stands at `now`: the page, how many characters show, whether the page is
// fully shown and whether the whole run is (`atEnd` — choices may appear).
export function revealView(state, pages, now, cps = TYPE_CPS) {
  const st = state || revealStart(now)
  if (!pages || pages.length === 0)
    return {
      page: null,
      index: 0,
      chars: 0,
      total: 0,
      pageDone: true,
      atEnd: true,
    }
  const index = Math.min(st.index, pages.length - 1)
  const page = pages[index]
  const total = partsLength(pageParts(page))
  const typed =
    cps === Infinity
      ? total
      : Math.floor((Math.max(0, now - st.startedAt) * cps) / 1000)
  const chars = st.complete ? total : Math.min(total, typed)
  const pageDone = chars >= total
  return {
    page,
    index,
    chars,
    total,
    pageDone,
    atEnd: pageDone && index === pages.length - 1,
  }
}

// A tap / Space / Enter. 'completed' = the page jumped to fully shown; 'next' = moved to the next
// page; 'end' = the run was already complete (the caller decides: a reply advances the story, a
// scene waits on its choices).
export function revealAdvance(state, pages, now, cps = TYPE_CPS) {
  const view = revealView(state, pages, now, cps)
  const st = state || revealStart(now)
  if (!view.pageDone)
    return {
      state: { ...st, index: view.index, complete: true },
      event: 'completed',
    }
  if (view.index < pages.length - 1)
    return {
      state: { index: view.index + 1, startedAt: now, complete: false },
      event: 'next',
    }
  return { state: st, event: 'end' }
}

// ---- the sex scene's ending ----------------------------------------------------------------
// After the positions and the build-up, "Finish" plays the climax clip once with sound, then the
// finale slide carries the afterglow. Payload (paid, 2026-10-03): climax_video_url, climax_url
// (its poster/still), finale_url — each may be missing.

// What "Finish" opens: the clip, the still (no clip), or nothing (straight to the finale).
export function climaxStage(sex) {
  if (!sex) return 'none'
  if (sex.climax_video_url) return 'video'
  if (sex.climax_url) return 'still'
  return 'none'
}

// The finale's picture: finale_url; without one, the climax clip's last frame if it played,
// else the climax still, else the first position's still.
export function finaleView(sex, { videoPlayed = false } = {}) {
  const s = sex || {}
  if (s.finale_url) return { url: s.finale_url, videoFrame: false }
  if (videoPlayed && s.climax_video_url) return { url: null, videoFrame: true }
  const still =
    s.climax_url ||
    (Array.isArray(s.positions) &&
      s.positions[0] &&
      s.positions[0].still_url) ||
    null
  return { url: still, videoFrame: false }
}

// ---- positions unlocked through dialogue (2026-10-03) ----------------------------------------
// A paid sex scene's positions each carry `ask` — the PLAYER's request for that position, shown
// as a choice — and `line`, hers while in it. The scene opens in position 0; the choice menu
// offers the asks not yet taken; taking one switches to that clip and unlocks it (a chip to come
// back to it). With every position unlocked the menu goes and the build-up starts. A game whose
// positions do not all have an `ask` (published before asks existed) keeps the free switcher with
// the build-up running from entry.

export function positionsMode(positions) {
  const list = Array.isArray(positions) ? positions : []
  if (list.length === 0) return 'free'
  return list.every((p) => p && typeof p.ask === 'string' && p.ask.trim())
    ? 'ask'
    : 'free'
}

export function startPositions(positions) {
  const list = Array.isArray(positions) ? positions : []
  const mode = positionsMode(list)
  return {
    mode,
    current: 0,
    unlocked: mode === 'free' ? list.map((_, i) => i) : list.length ? [0] : [],
  }
}

// The choice menu: the asks of the positions not yet unlocked, in order.
export function positionAsks(state, positions) {
  if (!state || state.mode !== 'ask' || !Array.isArray(positions)) return []
  return positions
    .map((p, i) => ({ index: i, label: (p && p.ask ? p.ask : '').trim() }))
    .filter((c) => c.label && !state.unlocked.includes(c.index))
}

// Taking an ask: that position becomes current and unlocked. An unknown index changes nothing.
export function unlockPosition(state, positions, index) {
  if (!state || !Array.isArray(positions) || !Number.isInteger(index))
    return state
  if (index < 0 || index >= positions.length) return state
  const unlocked = state.unlocked.includes(index)
    ? state.unlocked
    : [...state.unlocked, index].sort((a, b) => a - b)
  return { ...state, unlocked, current: index }
}

// A chip: back to a position already unlocked; a locked one cannot be reached this way.
export function switchPosition(state, index) {
  if (!state || !state.unlocked.includes(index)) return state
  return { ...state, current: index }
}

// Whether the build-up runs: every position unlocked (always true in the free mode).
export function buildupActive(state, positions) {
  if (!state || !Array.isArray(positions) || positions.length === 0)
    return false
  return positions.every((_, i) => state.unlocked.includes(i))
}

// The build-up vignette: a soft white glow at the frame's edge that pulses faster and stronger
// as Finish nears — ~0.6 Hz and faint at the start, ~1.6 Hz and strong at the end, peaking once
// Finish unlocks. Reduced motion: no pulse (hz 0), only a gentle brightening. The rate moves in
// 0.1 Hz steps so the CSS animation is not restarted on every tick.
export function vignette(fraction, ready, reducedMotion = false) {
  const f = Math.min(1, Math.max(0, Number.isFinite(fraction) ? fraction : 0))
  if (reducedMotion)
    return {
      opacity: ready ? 0.5 : Math.round((0.12 + 0.33 * f) * 100) / 100,
      hz: 0,
    }
  if (ready) return { opacity: 0.7, hz: 1.6 }
  const opacity = Math.round((0.18 + 0.42 * f ** 1.5) * 100) / 100
  const hz = Math.round((0.6 + 1.0 * f ** 2) * 10) / 10
  return { opacity, hz }
}

// "Back to the scene" from the end card: the scene in its finished state — every position
// unlocked (a chip each), no asks left, and `finished` so Finish is available at once with no
// build-up. It reopens on the position they were last on (position 0 if that is not valid).
export function returnToScene(state, positions) {
  const list = Array.isArray(positions) ? positions : []
  const current =
    state &&
    Number.isInteger(state.current) &&
    state.current >= 0 &&
    state.current < list.length
      ? state.current
      : 0
  return {
    mode: state && state.mode ? state.mode : positionsMode(list),
    current,
    unlocked: list.map((_, i) => i),
    finished: true,
  }
}

// The vignette through the climax clip — the release (2026-10-03). `t` = seconds since the clip
// actually started playing (its `playing` event), null before that: the glow holds the build-up
// peak meanwhile. Then a FLARE: three fast, bright pulses over FLARE_SECONDS (peaks 1.0, above
// the build-up's 0.7), then a smooth decay over the rest of the clip into a soft, warm afterglow
// (opacity 0.25, `warm` 1 = amber instead of white), which the player fades out as the finale
// crosses in. Reduced motion: no pulses — one soft brighten, then the same decay.
export const FLARE_SECONDS = 1.5
const AFTERGLOW = 0.25
const BUILDUP_PEAK = 0.7

const round2 = (x) => Math.round(x * 100) / 100
const easeOutCubic = (k) => 1 - (1 - k) ** 3

export function climaxGlow(t, duration, reducedMotion = false) {
  if (t === null || t === undefined || !Number.isFinite(t) || t < 0)
    return { opacity: BUILDUP_PEAK, warm: 0, holding: true }
  const d = Number.isFinite(duration) && duration > 0 ? duration : 8
  const rise = reducedMotion ? 0.6 : FLARE_SECONDS
  if (t < rise) {
    if (reducedMotion)
      return {
        opacity: round2(BUILDUP_PEAK + 0.15 * (t / rise)),
        warm: 0,
        holding: false,
      }
    const s = Math.sin((Math.PI * t) / (FLARE_SECONDS / 3))
    return {
      opacity: round2(BUILDUP_PEAK + (1 - BUILDUP_PEAK) * s * s),
      warm: 0,
      holding: false,
    }
  }
  const from = reducedMotion ? BUILDUP_PEAK + 0.15 : BUILDUP_PEAK
  const k = Math.min(1, (t - rise) / Math.max(0.5, d - rise))
  return {
    opacity: round2(AFTERGLOW + (from - AFTERGLOW) * (1 - easeOutCubic(k))),
    warm: round2(easeOutCubic(k)),
    holding: false,
  }
}

// ---- the build-up and Finish (2026-10-03, revised) ------------------------------------------
// The build-up runs from the moment the sex scene starts, over the whole scene, while the player
// is still unlocking positions. Its length is the date's `buildup_seconds` when it gives one,
// else POSITION_SECONDS per position (four positions -> 40 s).
export const POSITION_SECONDS = 10

export function sceneBuildupSeconds(sex, positionCount) {
  if (sex && Number.isFinite(sex.buildup_seconds) && sex.buildup_seconds > 0)
    return sex.buildup_seconds
  const n =
    Number.isInteger(positionCount) && positionCount > 0 ? positionCount : 1
  return POSITION_SECONDS * n
}

// Finish enables as soon as the build-up bar is full. Unlocking every position is optional
// (product owner, 2026-10-03): the ask menu stays open after Finish enables, so they can unlock
// more or finish. Back in the finished scene (returnToScene) it is enabled at once.
export function finishState({ ready, finished }) {
  return { enabled: Boolean(finished || ready) }
}

// ---- chapters (v2) ---------------------------------------------------------------------------

export function isV2(game) {
  return Boolean(game && Number(game.schema_version) >= 2)
}

// A choice whose `requires` are not all held is shown locked (greyed, with its hint) and cannot be
// taken. v1 choices have no `requires`, so nothing is ever locked there.
export function choiceLocked(choice, held) {
  const req = choice && Array.isArray(choice.requires) ? choice.requires : []
  if (!req.length) return false
  const have = new Set(Array.isArray(held) ? held : [])
  return !req.every((m) => have.has(m))
}

// The choice menu for a scene: every choice with whether it is locked and its hint.
// A choice's tone (2026-10-06), shown as an icon beside it: how the line reads, never where it leads. The backend
// drops anything else, and so does this, so an unknown tone shows no icon rather than a wrong one.
export const CHOICE_TONES = ['sweet', 'playful', 'bold', 'quiet']

export function choiceTone(choice) {
  return choice && CHOICE_TONES.includes(choice.tone) ? choice.tone : null
}

export function choiceViews(game, nodeId, held) {
  return choicesFor(game, nodeId).map((c) => {
    const locked = choiceLocked(c, held)
    return { ...c, locked, hint: locked ? c.locked_hint || null : null }
  })
}

// The memories a choice adds that were not held yet — each gets a toast and a `memory` event.
export function newMemories(held, sets) {
  const have = new Set(Array.isArray(held) ? held : [])
  return uniq((Array.isArray(sets) ? sets : []).filter((m) => !have.has(m)))
}

// The act whose card shows on entering this node (its `start`), with its 1-based number; null
// for any other node and for v1 dates.
export function actStarting(game, nodeId) {
  if (!isV2(game) || !Array.isArray(game.acts)) return null
  const i = game.acts.findIndex((a) => a && a.start === nodeId)
  return i < 0 ? null : { ...game.acts[i], number: i + 1 }
}

// The ladder rung reached on entering this node ("First kiss"), or null.
export function reachedRung(node) {
  const r = node && node.reached
  return r && r.label ? { id: r.id || null, label: r.label } : null
}

// A scene's small-win clip (v2): a short loop over its image.
export function sceneClip(node) {
  if (!node || (node.kind !== 'scene' && node.kind !== 'ending')) return null
  const shot = node.shot
  if (!shot || !shot.video_url) return null
  return { src: shot.video_url, poster: shot.image_url || null }
}

// This chapter's slice of the progress the API returns: v2 may nest per chapter
// (`chapters: {"<n>": {...}}`), v1 is flat.
export function chapterProgress(progress, chapter) {
  if (!progress || typeof progress !== 'object') return {}
  const nested = progress.chapters && progress.chapters[String(chapter)]
  return nested && typeof nested === 'object' ? nested : progress
}

// Memories a chapter opens with: those held when the previous chapter was won (`carry`).
export function carriedMemories(progress, chapter) {
  const carry = progress && progress.carry
  if (!carry || Number(carry.chapter) !== Number(chapter) - 1) return []
  return uniq(Array.isArray(carry.memories) ? carry.memories : [])
}

// What a paid resume at `checkpoint` restores: the memories held when this play reached it, else
// the chapter's opening memories.
export function resumeMemories(play, checkpoint, opening) {
  const snap = play && play.snapshots && play.snapshots[checkpoint]
  return uniq(
    Array.isArray(snap) ? snap : Array.isArray(opening) ? opening : [],
  )
}

// The finish choice: v2 always offers its finishes as choices (even one); v1 finishes directly.
export function finishChoices(sex) {
  if (!sex || sex.locked || sex.v !== 2) return []
  return sex.finishes.map((f, i) => ({
    index: i,
    id: f.id,
    label: f.label || f.id,
  }))
}

// The progress event for choosing a finish (v2): `ended` on the milestone with its finish_id and
// the memories held (the server writes them into `carry` for the next chapter).
export function finishEvent(nodeId, finishId, held, chapter) {
  return {
    event: 'ended',
    node_id: nodeId,
    finish_id: finishId,
    chapter,
    memories: uniq(Array.isArray(held) ? held : []),
  }
}

// The chapter's paths (v2, 2026-10-06): its route acts — every act but the one the story opens in —
// each found once any of its checkpoints or endings is in progress (local too, so guests count). With
// `hint`: the first path not found yet that is entered by a locked choice, in that choice's own
// locked_hint, the words the split already shows greyed out. Launch data: 155 of 249 players only
// ever saw one route, and nothing told them the others existed. null for v1 or a single route.
export function pathsCatalog(game, progress) {
  if (!isV2(game) || !Array.isArray(game.acts)) return null
  const startAct = getNode(game, game.start)?.act
  const routes = game.acts.filter((a) => a && a.id && a.id !== startAct)
  if (routes.length < 2) return null
  const reached = [
    ...((progress && progress.checkpoints) || []),
    ...((progress && progress.endings) || []),
  ]
  const seen = new Set(
    reached.map((id) => getNode(game, id)?.act).filter(Boolean),
  )
  const items = routes.map((a) => ({
    id: a.id,
    title: a.title || null,
    found: seen.has(a.id),
  }))
  let hint = null
  for (const route of routes) {
    if (seen.has(route.id)) continue
    for (const n of Object.values(game.nodes || {})) {
      const entry = ((n && n.choices) || []).find(
        (c) => c && c.to === route.start && c.locked_hint,
      )
      if (entry) {
        hint = entry.locked_hint
        break
      }
    }
    if (hint) break
  }
  return {
    found: items.filter((i) => i.found).length,
    total: items.length,
    items,
    hint,
  }
}

// Whether holding `memoryId` is what opens a path (a choice into a route's first scene requires
// it): its toast says so, where any other memory just says she'll remember (2026-10-06).
export function memoryOpensPath(game, memoryId) {
  if (!isV2(game) || !Array.isArray(game.acts)) return false
  const starts = new Set(game.acts.map((a) => a && a.start).filter(Boolean))
  return Object.values(game.nodes || {}).some((n) =>
    ((n && n.choices) || []).some(
      (c) =>
        c &&
        starts.has(c.to) &&
        Array.isArray(c.requires) &&
        c.requires.includes(memoryId),
    ),
  )
}

// The endings collection for a chapter ("2/7 endings"): fail endings and wins (milestones and,
// from 2026-10-04, win endings), found or locked. A locked fail shows its hint; a locked best win
// is "a secret ending" (a win ending is never the best one). `caught` marks a caught fail.
export function endingsCatalog(game, progress) {
  const found = new Set(
    progress && Array.isArray(progress.endings) ? progress.endings : [],
  )
  const items = []
  for (const [id, n] of Object.entries((game && game.nodes) || {})) {
    if (!n) continue
    if (n.kind === 'ending') {
      const win = nodeView(n) === 'win'
      items.push({
        id,
        kind: win ? 'win' : 'fail',
        title: (n.ending && n.ending.title) || null,
        hint: win ? null : (n.ending && n.ending.hint) || null,
        found: found.has(id),
        secret: false,
        caught: isCaught(n),
      })
    } else if (n.kind === 'milestone' || n.kind === 'sex') {
      const s = sexOf(n)
      const best = Boolean(s && !s.locked && s.best)
      items.push({
        id,
        kind: 'win',
        title: (s && s.title) || null,
        hint: null,
        found:
          found.has(id) ||
          (n.kind === 'sex' && Boolean(progress && progress.won)),
        secret: best,
        caught: false,
      })
    }
  }
  items.sort((a, b) => (a.kind === b.kind ? 0 : a.kind === 'win' ? -1 : 1))
  return {
    found: items.filter((i) => i.found).length,
    total: items.length,
    items,
  }
}

// Whether a win's end card says "There's a better ending": a v2 win that is not the chapter's
// best — an unlocked milestone not marked `best`, or any win ending (2026-10-04: a win ending has
// no `best` flag; the secret best ending is a milestone).
export function showsBetterEnding(game, node) {
  if (nodeView(node) === 'win') return isV2(game)
  const s = sexOf(node)
  return Boolean(s && !s.locked && s.v === 2 && !s.best)
}

// The chapter after this one, for "Continue to Chapter n+1": published chapters only; locked for
// Free and guests (chapter 2+ is for every paid plan).
export function nextChapter(chapters, current, planUnlocked) {
  const list = Array.isArray(chapters) ? chapters : []
  const next = list.find((c) => c && Number(c.chapter) === Number(current) + 1)
  if (!next) return null
  return {
    chapter: Number(next.chapter),
    title: next.title || null,
    locked: !planUnlocked,
  }
}

// The chapter's display name: "Chapter 1: Summer Heat" (v2), the title alone for v1.
export function chapterLabel(game, format) {
  if (!game) return ''
  if (!isV2(game)) return game.title || ''
  return format(game.chapter ?? 1, game.title || '')
}

// ---- the Explore Stories row (2026-10-04) -----------------------------------------------------

// The images a story card cycles through: the feed's `showcase` (up to three non-explicit stills
// chosen at publish), else its cover. Nothing else from the story is ever shown on Explore.
export function storyShowcase(game) {
  const list = (
    game && Array.isArray(game.showcase) ? game.showcase : []
  ).filter((u) => typeof u === 'string' && u)
  if (list.length) return list.slice(0, 3)
  return game && game.cover_url ? [game.cover_url] : []
}

// Where a card's button leads for this browser's player: null = not started ("Play"), else the
// chapter to continue. `state(n)` returns what this browser saved for chapter n:
// { play: {path}, progress: {won, endings, checkpoints} }. The latest chapter they touched wins;
// a won chapter continues into the next published one.
export function storyContinue(chapterNumbers, state) {
  const nums = [
    ...new Set((chapterNumbers || []).map(Number).filter((n) => n >= 1)),
  ].sort((a, b) => a - b)
  if (!nums.length) nums.push(1)
  let target = null
  for (const n of nums) {
    const s = (typeof state === 'function' && state(n)) || {}
    const played =
      (s.play && Array.isArray(s.play.path) && s.play.path.length > 1) ||
      (s.progress &&
        (s.progress.won ||
          (Array.isArray(s.progress.endings) && s.progress.endings.length) ||
          (Array.isArray(s.progress.checkpoints) &&
            s.progress.checkpoints.length)))
    if (!played) continue
    target = n
    if (s.progress && s.progress.won && nums.includes(n + 1)) target = n + 1
  }
  return target === null ? null : { chapter: target }
}
